//BazaDanychPage
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Nextjs */
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

/** MUI */
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Badge, Button, Container, Grid, NoSsr, Typography, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

/** Redux */
import { setSkipLinksPage } from "@/src/_appStore/Common";
import { useDispatch, useSelector } from "react-redux";
import {
    getAreaForPanel,
    getFetched,
    getIsToApply,
    getLeftDrawerOpen,
    getLeftDrawerWidth,
    getTableOrChart,
    setAreaForPanel,
    setCustomerSection,
    setLeftDrawerOpen,
    setPrediction,
    setPredictionLoading,
    setTableOrChart,
} from "../../_appStore/DB_Common";
import { setDatesSelected } from "../../_appStore/DB_Dates";
import * as ChartsFS from "../../_appStore/DB_Filters_Charts";
import * as TableFS from "../../_appStore/DB_Filters_Table";
import { getSelectedIndicators, setCurrentIndicator, setSelectedIndicators } from "../../_appStore/DB_Indicators";
import { setSelectedTerritorialUnitsItem } from "../../_appStore/DB_JTs";

/** API */
import { getIndicatorsTreeFromAPI, getVariableSectionsFromAPI } from "../../_APIs";

/** Helpers */
import WebWorker from "@/src/_common/WebWorker";
import uniqBy from "lodash/uniqBy";
import { useDebounce } from "use-debounce";
import useHelper from "../../_common/useHelper";
import { PredictionWorker } from "./utilites/predictionWorker";

/** matomo */
import { useMatomo } from "@datapunt/matomo-tracker-react";

/** UI */
import PagePreloader from "@/src/_common/PagePreloader";
import ComponentIsUnderConstruction from "../../_common/ComponentIsUnderConstruction";
import SwipeableLeftDrawer from "./Drawer/SwipeableLeftDrawer";
import Steps from "./Steps/Steps";
import SronlyH2 from "@/src/components/srOnly/SronlyH2";
import AppTooltip from "@/src/_common/AppTooltip";
import Preloader from "@/src/_common/Preloader";
import DatesRangeSkeleton from "./skeletons/DatesRangeSkeleton";
import TerritorialUnitsRangeSkeleton from "./skeletons/TerritorialUnitsRangeSkeleton";
import RepositoryDrawer from "../RepozytoriumPage/Dialogs/RepositoryDrawer";

// const SwipeableLeftDrawer = dynamic(() => import("./Drawer/SwipeableLeftDrawer"));
// const DBChart = dynamic(() => import("./Charts/_Chart"));
// const Steps = dynamic(() => import("./Steps/Steps"), { loading: () => <StepsPreloader /> });

const DatesRange = dynamic(() => import("./DatesRange/_DatesRange"), { loading: () => <DatesRangeSkeleton /> });
const TerytorialUnitsRange = dynamic(() => import("./TerritorialUnitsRange/_TerritorialUnitsRange"), {
    loading: () => <TerritorialUnitsRangeSkeleton />,
});
const IndicatorsChips = dynamic(() => import("./IndicatorsChips/IndicatorsChips"));
const Filters = dynamic(() => import("./Filters/_Filters"), { loading: () => <Preloader type={"linear"} /> });
const DBTable = dynamic(() => import("./Table/_Table"), { loading: () => <Preloader type={"linear"} /> });
const Collapsor = dynamic(() => import("./components/Collapsor"));
const StatusBar = dynamic(() => import("./Status/StatusBar"));

const useStyles = makeStyles((theme) => ({
    root: {
        paddingBottom: 1,
    },
    SelectMessageHolder: {
        position: "relative",
        padding: 0, // theme.spacing(0, 2, 0, 10),
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(0, 2, 0, 6),
        },
    },
    DatesJTsHolder: {
        marginTop: theme.spacing(4),
        [theme.breakpoints.down("lg")]: {
            marginTop: theme.spacing(3),
            padding: theme.spacing(0, 2.5, 0, 2),
        },
        [theme.breakpoints.down("md")]: {
            marginTop: theme.spacing(2),
            padding: theme.spacing(0, 2.5, 0, 1.5),
            maxWidth: "100vw",
        },
        "@media (min-width: 750px)": {
            padding: theme.spacing(0, 2.5, 0, 8),
        },
        "@media (max-width: 319px)": {
            marginLeft: 0,
            marginTop: theme.spacing(8),
            padding: theme.spacing(0, 2.5, 0, 2.5),
        },
    },
    DatesJTsGridContainer: {
        justifyContent: "center",
        marginLeft: theme.spacing(-1.5),
        [theme.breakpoints.down("lg")]: {
            justifyContent: "flex-end",
            "& $gridItemDates + $gridItemJTs": {
                marginLeft: theme.spacing(1),
            },
        },
    },
    gridItemDates: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(3, 3),
        display: "flex",
        alignItems: "center",
        // minWidth: 588,
        minWidth: "calc(50vw - 112px)",
        "& > svg": {
            color: theme.palette.common.black,
        },
        [theme.breakpoints.up("xl")]: {
            minWidth: 588,
        },
        [theme.breakpoints.down("lg")]: {
            maxWidth: "calc(50% - 8px)",
            // minWidth: "calc(50% - 64px)",
            minWidth: "calc(50% - 16px)",
        },
        // [theme.breakpoints.down("md")]: {
        "@media (max-width: 750px)": {
            width: "calc(100% - 4px)",
            minWidth: "calc(100% - 64px)",
            maxWidth: "100%",
            marginRight: 0,
            marginBottom: theme.spacing(3),
            marginLeft: 64,
            "&, &.MuiGrid-item": {
                padding: theme.spacing(2, 2, 2, 2),
            },
        },
        "@media (max-width: 319px)": {
            minWidth: "100%",
            marginRight: 0,
            marginBottom: theme.spacing(3),
            "&, &.MuiGrid-item": {
                padding: theme.spacing(2, 2, 2, 2),
            },
        },
    },
    gridItemJTs: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(3, 3),
        marginLeft: theme.spacing(5),
        display: "flex",
        alignItems: "center",
        // maxWidth: 588,
        maxWidth: "calc(50vw - 112px)",
        "& > svg": {
            color: theme.palette.common.black,
        },
        [theme.breakpoints.up("xl")]: {
            maxWidth: 588,
        },
        [theme.breakpoints.down("lg")]: {
            maxWidth: "calc(50% - 8px)",
            minWidth: "calc(50% - 64px)",
        },
        // [theme.breakpoints.down("md")]: {
        "@media (max-width: 750px)": {
            width: "calc(100% - 4px)",
            minWidth: "calc(100% - 12px)",
            maxWidth: "100%",
            marginRight: 0,
            marginBottom: theme.spacing(3),
            "&, &.MuiGrid-item": {
                padding: theme.spacing(2, 2, 2, 2),
            },
        },
        "@media (max-width: 319px)": {
            minWidth: "100%",
            marginRight: 0,
            marginBottom: theme.spacing(3),
            "&, &.MuiGrid-item": {
                padding: theme.spacing(2, 2, 2, 2),
            },
        },
    },
    TableOrChartHolder: {
        margin: theme.spacing(5, 0, 0, 0),
        padding: theme.spacing(0, 5, 0, 5),
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(0, 1, 0, 2),
            margin: theme.spacing(1, 0),
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 1, 0, 1),
            margin: theme.spacing(1, 0),
            "&, &.MuiGrid-item": {
                padding: theme.spacing(0, 1, 2, 1),
            },
        },
    },
    TableHolder: {
        scrollMargin: "124px",
        margin: theme.spacing(5, 0, 5, 0),
        padding: theme.spacing(0, 5, 0, 5),
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(0, 1, 0, 2),
            margin: theme.spacing(1, 0),
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 1, 0, 1),
            margin: theme.spacing(1, 0),
        },
    },
    FiltersHolder: {
        // margin: theme.spacing(0, 0, 5, 0),
        margin: theme.spacing(6, 0, 3, 0),
        padding: theme.spacing(0, 5, 0, 5),
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(0, 1, 0, 2),
            margin: theme.spacing(5, 0, 1, 0),
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 1, 0, 1),
            margin: theme.spacing(1, 0, 3, 0),
        },
    },
    TableOrChart: {
        fontSize: "0.75rem",
        padding: theme.spacing(1.5, 4.5),
    },
    TableOrChartActive: {
        backgroundColor: `${theme.palette.primary.colors["50"]}!important`,
    },
    TableOrChartToolbox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        minWidth: 88,
    },
    boxedWrapper: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(3, 3),
        minHeight: theme.spacing(10), //96,
        position: "relative",
        display: "flex",
        alignItems: "center",
        minHeight: 110,
        [theme.breakpoints.down("sm")]: {
            minHeight: "unset",
            padding: theme.spacing(2.5, 3),
        },
    },
    boxedWrapperMargin: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(0, 3),
        // minHeight: theme.spacing(10), //96,
        position: "relative",
        display: "flex",
        alignItems: "center",
        // minHeight: 110,
        [theme.breakpoints.down("sm")]: {
            minHeight: "unset",
            padding: theme.spacing(2.5, 3),
            "&, &.MuiGrid-item": {
                padding: theme.spacing(0.5, 1, 0.5, 1),
            },
        },
    },
    openLeftRoot: {
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.66666667rem",
            padding: theme.spacing(1),
        },
    },
    openLeftstartIcon: {
        "& svg": {
            fontSize: "inherit!important",
        },
        [theme.breakpoints.down("sm")]: {
            marginRight: 2,
        },
    },
    boxedWrapperTitle: {
        // position: "absolute",
        // top: -16,
        padding: theme.spacing(0, 0),
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(2.5),
        alignSelf: "baseline",
        fontSize: "1.11111111rem", // "0.875rem",
        color: theme.palette.primary.dark,
        background: `linear-gradient(transparent 0%, transparent 50%, #FFF 50%, #FFF 100%);`,
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    boxTitle: {
        maxWidth: 96,
        display: "flex",
        alignItems: "center",
    },
    boxContent: {
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            justifyContent: "flex-start",
        },
    },
    boxedMessageWrapper: {
        ...theme.shape.boxedWrapper,
        backgroundColor: "#F8F9FA",
        minHeight: theme.spacing(11.5), //theme.spacing(10), //96,
        maxWidth: 600,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        marginTop: 0, //theme.spacing(5),
        marginLeft: theme.spacing(7),
    },
    separator: {
        backgroundColor: "#000",
        width: "98%",
        margin: "0 auto",
        opacity: 0.0675,
    },
    resizer: {
        backgroundColor: "#744FAC",
        cursor: "ew-resize",
        width: 0,
        position: "absolute",
        right: 80,
        top: 16,
        height: "100%",
        zIndex: 2101,
        opacity: 0.025,
        "&:hover": {
            opacity: 1,
        },
        "&:after": {
            content: '" "',
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 20,
            height: 48,
            borderRadius: 20,
            background: `#744FAC url('data:image/svg+xml,%3Csvg xmlns="http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg" width="24" height="24" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"%3E%3Cpath fill="white" d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2s.9-2 2-2s2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2s-2 .9-2 2s.9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2z"%2F%3E%3C%2Fsvg%3E') center center no-repeat`,
        },
    },
    stepsHolder: {
        display: "flex",
        justifyContent: "center",
        "@media only screen and (min-width: 1921px)": {
            justifyContent: "flex-start",
        },
    },
}));

const BazaDanychIndex = (props) => {
    // process.env.NODE_ENV === "development" &&
    //     console.log("%c BazaDanychIndex ", "background: #660; color: #fff;", props);

    const { i18n, t } = useTranslation();
    const classes = useStyles();
    const router = useRouter();
    const dispatch = useDispatch();
    const { getAllIDs, intlNumberFormat } = useHelper();
    const { trackPageView } = useMatomo();
    const predictionData = useRef();

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    /** Redux selectors */
    const areaForPanel = useSelector(getAreaForPanel);
    const leftDrawerOpen = useSelector(getLeftDrawerOpen);
    const leftDrawerWidth = useSelector(getLeftDrawerWidth);
    const selectedIndicators = useSelector(getSelectedIndicators);
    const selectedPositions = useSelector(TableFS.getSelectedPositions);
    const currentSection = useSelector(TableFS.getCurrentSection);

    const multiSections = useSelector(TableFS.getMultiSections);
    // console.log(` ----------------------------`)
    // console.log(`multiSections:`, multiSections)
    // console.log(` ----------------------------`)

    const _fetched = useSelector(getFetched);
    /** - table or Chart switch */
    const tableOrChart = useSelector(getTableOrChart);
    const isToApply = useSelector(getIsToApply);
    // const prediction = useSelector(getPrediction);

    /** States */
    const [IS_TO_BE_APPLIED, setIS_TO_BE_APPLIED] = useState(false);
    const [APPLY, setAPPLY] = useState(false);
    const [IS_LOADING_DEFAULTS, setIS_LOADING_DEFAULTS] = useState(false);
    const [IS_DEFAULTS_ERROR, setIS_DEFAULTS_ERROR] = useState(false);
    const [LOADING, setLOADING] = useState(Object.keys(router?.query)?.length);
    const [RECORDS_COUNT, setRECORDS_COUNT] = useState(0);
    const [SELECTED_INDICATORS, setSELECTED_INDICATORS] = useState(null);
    const [tableOrChartActive, setTableOrChartActive] = useState("table");
    const [PREDICTION_WORKER, setPREDICTION_WORKER] = useState(null);
    const [PREDICTION_PARAMS, setPREDICTION_PARAMS] = useState(null);
    const [SELECTED_POSITIONS, setSELECTED_POSITIONS] = useState([]);

    const [leftDrawerState] = useDebounce(leftDrawerOpen, 1000);

    /** Effects */

    useEffect(() => {
        if (router?.query?.id) {
            setLOADING(true);
        }
    }, [router]);

    useEffect(() => {
        // useLayoutEffect(() => {
        const PATHNAME = router?.pathname;

        // const HREF = `${window?.location?.origin}/${window?.location?.pathname}`;
        const HREF = `${window?.location?.origin}${window?.location?.pathname}`;

        // console.log(HREF);

        if (!_fetched) return;

        if (router?.query?.group_id) {
            const _newA4P = { group_id: router?.query?.group_id };

            if (JSON.stringify(areaForPanel) !== JSON.stringify(_newA4P)) {
                dispatch(setAreaForPanel(_newA4P));
            }

            setTimeout(() => {
                // router.push(router.pathname);
                window.location.href = HREF;
            }, 3000);
        }

        if (router?.query?.id) {
            dispatch(setLeftDrawerOpen(true));
            const setByQuery = async () => {
                try {
                    const IDs = router?.query?.id.split(",");
                    const _customer_section = router?.query?.s;

                    await getIndicatorsById({ IDs, getAllIDs })
                        .then(async (result) => {
                            // process.env.NODE_ENV === "development" && console.log({ result, IDs, router });

                            if (result?.message?.type === "Error" || !result?.length) {
                                process.env.NODE_ENV === "development" &&
                                    console.log("%c getIndicatorsById error: ", "background: #F00; color:#FFF", result);
                                router.push(`/${i18n.language}/404`);
                                return;
                            } else {
                                setLOADING(false);
                            }

                            if (result instanceof Array) {
                                const _newSelectedIndicators = uniqBy(
                                    [...(result || []), ...(selectedIndicators || [])],
                                    "indicator_id"
                                );

                                await Promise.all([
                                    dispatch(setSelectedTerritorialUnitsItem([])),
                                    dispatch(setDatesSelected([])),

                                    // dispatch(setCurrentIndicator(null)),

                                    // dispatch(TableFS.setCurrentSection(null)),
                                    dispatch(TableFS.setDefaults(null)),

                                    dispatch(ChartsFS.setCurrentSection(null)),
                                    dispatch(ChartsFS.setDefaults(null)),
                                ])
                                    .then(() => {
                                        // dispatch(setSelectedIndicators(result));
                                        dispatch(setSelectedIndicators(_newSelectedIndicators));
                                        dispatch(setCurrentIndicator(result[0]));
                                    })
                                    .then(() => {
                                        dispatch(setAreaForPanel(null));
                                        if (_customer_section && !isNaN(Number(_customer_section))) {
                                        } else {
                                            dispatch(setLeftDrawerOpen(false));
                                        }

                                        // setTimeout(() => {
                                        //     window.location.href = HREF;
                                        // }, 3000);

                                        router.replace(router.pathname);
                                    });
                            }
                        })
                        .catch((error) => {
                            console.log(error);
                        })
                        .finally(() => {});

                    // const _customer_section = router?.query?.s;
                    if (_customer_section && !isNaN(Number(_customer_section))) {
                        const indicatorId = IDs?.[0];
                        const years = [2010];
                        const periods = [282];

                        await getVariableSectionsFromAPI({ indicatorId, years, periods })
                            .then((result) => {
                                if (result.message && result.message.type === "Error") {
                                } else {
                                    // const _section = {
                                    //     [indicatorId]: result?.find(
                                    //         (r) => r.id_section?.toString() === _customer_section
                                    //     ),
                                    // };

                                    const _newCurrentSection = {
                                        ...structuredClone(currentSection || {}),
                                        [indicatorId]: result?.find(
                                            (r) => r.id_section?.toString() === _customer_section
                                        ),
                                    };

                                    dispatch(TableFS.setCurrentSection(_newCurrentSection));

                                    const _newMultiSections = {
                                        ...multiSections,
                                        [indicatorId]: [Number(_customer_section)],
                                        enabled: false,
                                    };

                                    dispatch(TableFS.setMultiSections(_newMultiSections));
                                }
                            })
                            .catch((error) => {
                                console.log(
                                    "%c getVariableSectionsFromAPI error: ",
                                    "background: #F00; color:#FFF",
                                    error
                                );
                            })
                            .finally(() => {});

                        dispatch(setCustomerSection(Number(_customer_section)));
                        dispatch(setLeftDrawerOpen(false));
                    }

                    // return <span />;
                } catch (error) {
                    console.log(error);
                    router.push("/500");
                    // return <span />;
                }
            };

            setByQuery();
        }
    }, [
        _fetched,
        // currentSection
    ]);

    // useEffect(() => trackPageView(), []);

    useEffect(() => {
        dispatch(setLeftDrawerOpen(!selectedIndicators?.length));
    }, [selectedIndicators]);

    useEffect(() => {
        if (areaForPanel) {
            dispatch(setLeftDrawerOpen(true));
        }
    }, [areaForPanel]);

    useEffect(() => {
        setTableOrChartActive(tableOrChart);
    }, [tableOrChart]);

    useEffect(() => {
        const predictionWorker = new WebWorker(PredictionWorker);
        predictionWorker.onmessage = function (event) {
            let _predictionResult = event.data;
            const { _errMessageShort, _acceptedInfo, COUNTER, _limits } = _predictionResult;

            /** messages */
            // _predictionResult._errMessageShort = _errMessageShort
            //     ? t("Uwaga! Limit został przekroczony{{dot}}", { dot: "." })
            //     : "";
            _predictionResult._errMessageShort = _errMessageShort
            ? t("Przekroczono maksymalną liczbę rekordów{{dot}} Zmniejsz liczbę wybranych opcji{{dot}}", { dot: "." })
            : "";

            let _errMessage = "";

            _errMessage += _acceptedInfo.cols
                ? ""
                : t("Wybrana liczba kolumn ({{selected}}) przekracza limit ({{limit}}){{dot}}", {
                      selected: intlNumberFormat(COUNTER.columns || 0, 0),
                      limit: intlNumberFormat(_limits.columns || 0, 0),
                      dot: ".",
                  });

            _errMessage += _acceptedInfo.rows
                ? ""
                : t("Wybrana liczba wierszy ({{selected}}) przekracza limit ({{limit}}){{dot}}", {
                      selected: intlNumberFormat(COUNTER.rows || 0, 0),
                      limit: intlNumberFormat(_limits.rows || 0, 0),
                      dot: ".",
                  });

            _predictionResult._errMessage = _errMessage;

            const _prediction = {
                [tableOrChart]: {
                    ...(_predictionResult || {}),
                    _loading: false,
                },
            };
            dispatch(setPrediction(_prediction));
            // setPREDICTION_LOADING(false);
            dispatch(setPredictionLoading(false));
        };
        setPREDICTION_WORKER(predictionWorker);
        return () => {
            predictionWorker.terminate();
        };
    }, []);

    useEffect(() => {
        const _skipLinksPage = leftDrawerState ? "dbStart" : "dbFull";
        dispatch(setSkipLinksPage({ id: _skipLinksPage }));
        return () => dispatch(setSkipLinksPage(""));
    }, [leftDrawerState]);

    /** Handlers */
    const handleTableOrChart = (type) => {
        if (type !== tableOrChartActive) {
            dispatch(setTableOrChart(type));
        }
    };

    const handleOnLoadingDefaults = ({ loading }) => {
        setIS_LOADING_DEFAULTS(loading);
    };

    const handleOnDefaultsError = ({ error }) => {
        setIS_DEFAULTS_ERROR(error);
    };

    const handleOnIsToBeApplied = (value) => {
        setIS_TO_BE_APPLIED(value);
    };

    const handleOnApply = (value) => {
        setAPPLY(value);
    };

    const handleOnRecordsCount = (args) => {
        setRECORDS_COUNT(args);
    };

    const handleOnPredictionData = (data) => {
        data.CUSTOM_SELECTED_POSITIONS = data.CUSTOM_SELECTED_POSITIONS || SELECTED_POSITIONS || selectedPositions;

        if (PREDICTION_WORKER) {
            try {
                
                const _predictionWorkerParams = { ...data, PREDICTION_DATA: data };
                const _predictionParams = { ...data, from: undefined };
                delete _predictionParams.from;
    
                if (JSON.stringify(_predictionParams) !== JSON.stringify(PREDICTION_PARAMS)) {
                    PREDICTION_WORKER.postMessage(_predictionWorkerParams);
                    setPREDICTION_PARAMS(_predictionParams);
    
                    dispatch(setPredictionLoading(true));
                }
            } catch (error) {
                console.log(error)
                
            }
        }
        return;
    };

    const handleOnSelectedIndicators = (items) => {
        setSELECTED_INDICATORS(items);
    };

    const handleOnSelectPositions = (newPositions) => setSELECTED_POSITIONS(newPositions);

    const handleOpenLeftDrawer = () => {
        dispatch(setLeftDrawerOpen(true));
        dispatch(setAreaForPanel(null));
    };

    return (
        <>
            {!!LOADING && <PagePreloader />}
            <div className={classes.root}>
                <SwipeableLeftDrawer onSelectedIndicators={(items) => handleOnSelectedIndicators(items)} />

                {/*** Repository ***/}
                {/* {!!leftDrawerOpen && <RepositoryDrawer source={"db"} hideExport={!!leftDrawerOpen} icon={true} />} */}

                {!leftDrawerOpen ? (
                    <NoSsr>
                        {/*** Dates and JTs ***/}
                        <Container maxWidth={false} className={classes.DatesJTsHolder}>
                            <SronlyH2>{t("Zmiana szeregu czasowego oraz jednostki terytorialnej")}</SronlyH2>
                            {IS_DEFAULTS_ERROR ? (
                                <span style={{ height: 64, display: "block" }}></span>
                            ) : (
                                <Grid container spacing={3} className={classes.DatesJTsGridContainer}>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={2}
                                        className={classes.gridItemDates}
                                        sx={{ position: "relative" }}
                                    >
                                        <DatesRange
                                            onPredictionData={(data) => handleOnPredictionData(data)}
                                            tableOrChart={tableOrChart}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md
                                        className={classes.gridItemJTs}
                                        sx={{ position: "relative" }}
                                    >
                                        <TerytorialUnitsRange
                                            onPredictionData={(data) => handleOnPredictionData(data)}
                                            tableOrChart={tableOrChart}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Container>
                    </NoSsr>
                ) : null}

                {/*** Repository ***/}
                <RepositoryDrawer source={"db"} hideExport={!!leftDrawerOpen} icon={true} />

                {!leftDrawerOpen ? (
                    <NoSsr>
                        {/*** Switch table/chart view and indicators ***/}
                        <Container maxWidth={false} className={classes.TableOrChartHolder}>
                            <Grid container spacing={isMobile ? 0 : 3}>
                                {isMobile ? (
                                    <Grid item xs={12} style={{ display: "none" }}>
                                        <Typography variant={"body1"} sx={{ ml: 3, mb: 1, mt: 1 }}>
                                            {t("Widok")}
                                            {":"}
                                        </Typography>
                                    </Grid>
                                ) : null}

                                <Grid item xs={12} md={3} style={{ display: "none" }}>
                                    <Grid container spacing={0} className={classes.boxedWrapper}>
                                        <span className={classes.boxedWrapperTitle}>
                                            {t("Widok")}
                                            {":"}
                                        </span>
                                        <Grid item xs md className={classes.boxContent}>
                                            <AppTooltip
                                                title={
                                                    isToApply === "chart" && tableOrChartActive === "chart"
                                                        ? t(
                                                              "Aby przełączyć musisz zastosować lub zresetować ustawienia filtrów"
                                                          )
                                                        : ""
                                                }
                                                arrow
                                                placement={"top"}
                                                type={"dark"}
                                            >
                                                <span>
                                                    <Badge
                                                        variant={"dot"}
                                                        color={"primary"}
                                                        invisible={
                                                            !(isToApply === "table" && tableOrChartActive === "table")
                                                        }
                                                    >
                                                        <Button
                                                            fullWidth
                                                            color={"primary"}
                                                            size={"small"}
                                                            className={clsx(
                                                                classes.TableOrChart,
                                                                tableOrChartActive === "table" &&
                                                                    classes.TableOrChartActive
                                                            )}
                                                            onClick={() => handleTableOrChart("table")}
                                                            disabled={
                                                                isToApply === "chart" && tableOrChartActive === "chart"
                                                            }
                                                        >
                                                            {t("TABLICA")}
                                                        </Button>
                                                    </Badge>
                                                </span>
                                            </AppTooltip>
                                        </Grid>
                                        <Grid item xs md className={classes.boxContent}>
                                            <AppTooltip
                                                title={
                                                    isToApply === "table" && tableOrChartActive === "table"
                                                        ? t(
                                                              "Aby przełączyć musisz zastosować lub zresetować ustawienia filtrów"
                                                          )
                                                        : ""
                                                }
                                                arrow
                                                placement={"top"}
                                                type={"dark"}
                                            >
                                                <span>
                                                    <Badge
                                                        variant={"dot"}
                                                        color={"primary"}
                                                        invisible={
                                                            !(isToApply === "chart" && tableOrChartActive === "chart")
                                                        }
                                                    >
                                                        {/* <Button
                                                        fullWidth
                                                        color={"primary"}
                                                        size={"small"}
                                                        className={clsx(
                                                            classes.TableOrChart,
                                                            tableOrChartActive === "chart" && classes.TableOrChartActive
                                                        )}
                                                        onClick={() => handleTableOrChart("chart")}
                                                        disabled={
                                                            isToApply === "table" && tableOrChartActive === "table"
                                                        }
                                                    >
                                                        {t("Wykres")}
                                                    </Button> */}
                                                    </Badge>
                                                </span>
                                            </AppTooltip>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {isMobile ? (
                                    <Grid item xs={12}>
                                        <Typography variant={"body1"} sx={{ ml: 3, mb: 1, mt: { sm: 0, md: 3 } }}>
                                            {t("Zmienne")}
                                            {":"}
                                        </Typography>
                                    </Grid>
                                ) : null}

                                <SronlyH2>{t("Zmienne")}</SronlyH2>
                                <Grid item xs={12} md={12}>
                                    {/* <Grid container spacing={0} className={classes.boxedWrapper}> */}
                                    <Grid container spacing={0} className={classes.boxedWrapperMargin}>
                                        <Grid item xs={12} md={12} className={classes.boxContent}>
                                            {/*** Indicators chips ***/}

                                            <Collapsor
                                                leftButtonTitle={t("Zmienne")}
                                                rightButton={
                                                    <Button
                                                        startIcon={<ArrowForwardIosIcon />}
                                                        onClick={handleOpenLeftDrawer}
                                                        classes={{
                                                            root: classes.openLeftRoot,
                                                            startIcon: classes.openLeftstartIcon,
                                                        }}
                                                    >
                                                        {isMobile ? t("DODAJ/USUŃ") : t("DODAJ/USUŃ ZMIENNE")}
                                                    </Button>
                                                }
                                            >
                                                <IndicatorsChips />
                                            </Collapsor>

                                            {/* <span className={classes.boxedWrapperTitle}>
                                            {t("Zmienne")}
                                            {":"}
                                        </span>
                                        <IndicatorsChips /> */}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Container>

                        {/* <Divider className={classes.separator} /> */}

                        {/*** Filters ***/}
                        <Container maxWidth={false} className={classes.FiltersHolder}>
                            <SronlyH2>{t("Wybór zaawansowany")}</SronlyH2>
                            <Filters
                                onLoadingDefaults={(loading) => handleOnLoadingDefaults({ loading })}
                                onIsToBeApplied={(value) => handleOnIsToBeApplied(value)}
                                onRecordsCount={(args) => handleOnRecordsCount(args)}
                                onPredictionData={(data) => handleOnPredictionData(data)}
                                onIsDefaultsError={(error) => handleOnDefaultsError({ error })}
                                onSelectPositions={(newPositions) => handleOnSelectPositions(newPositions)}
                                onApply={handleOnApply}
                                apply={APPLY}
                            />
                        </Container>

                        {/* <Divider className={classes.separator} /> */}

                        {/*** Table ***/}
                        {IS_DEFAULTS_ERROR ? (
                            <></>
                        ) : (
                            <Container maxWidth={false} className={classes.TableHolder} id={"db-main-table"}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        {!tableOrChart || tableOrChart === "table" ? (
                                            <DBTable
                                                loadingDefaults={IS_LOADING_DEFAULTS}
                                                isToBeApplied={IS_TO_BE_APPLIED}
                                                onApply={handleOnApply}
                                                recordsCount={RECORDS_COUNT}
                                                onPredictionData={(data) => handleOnPredictionData(data)}
                                            />
                                        ) : (
                                            // <DBChart />
                                            <span
                                                style={{
                                                    height: 234,
                                                    display: "flex",
                                                    position: "relative",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <ComponentIsUnderConstruction />
                                                <em style={{ color: "#DDD" }}> {"Wykres"}</em>
                                            </span>
                                        )}
                                    </Grid>
                                </Grid>
                            </Container>
                        )}

                        {/*** Status ***/}
                        <StatusBar />
                    </NoSsr>
                ) : (
                    <NoSsr>
                        <Container maxWidth={false} className={classes.SelectMessageHolder}>
                            <Grid container spacing={0} sx={{ position: "relative" }}>
                                <Grid
                                    item
                                    xs={12}
                                    sm={1}
                                    md={1}
                                    lg={1}
                                    // sx={{ position: "relative", minWidth: { md: leftDrawerWidth + 80 } }}
                                    sx={{ position: "relative", minWidth: { md: leftDrawerWidth + 93 } }}
                                ></Grid>

                                <Grid item xs sm md lg className={classes.stepsHolder}>
                                    <Steps />
                                </Grid>
                            </Grid>
                        </Container>
                    </NoSsr>
                )}
            </div>
        </>
    );
};

// export default memo(BazaDanychIndex);
export default BazaDanychIndex;

const getIndicatorsById = async ({ IDs, getAllIDs }) => {
    let RESULT;
    await getIndicatorsTreeFromAPI()
        .then((result) => {
            // process.env.NODE_ENV === "development" &&
            //     console.log("%c getIndicatorsTreeFromAPI: ", "background: #CCC; color:#FFF");

            if (result.message && result.message.type === "Error") {
                RESULT = result;
            } else {
                RESULT = getAllIDs(result)
                    ?.filter((r) => r.indicator_id && IDs?.includes(`${r.indicator_id}`))
                    ?.map((r) => ({ ...r, uid: r.id }));
            }
        })
        .catch((error) => {
            console.log("%c getIndicatorsTreeFromAPI error: ", "background: #f00; color:#FFF", error);
        })
        .finally(() => {});

    return RESULT;
};
